import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { LlmChatUiActionAgentQuestion } from '@paladin-monorepo/common';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';

interface LlmChatAgentQuestionActionsProps {
  uiAction: LlmChatUiActionAgentQuestion;
  disabled?: boolean;
  onSelect: (answer: string) => void;
  className?: string;
}

export function getAgentQuestionDisplayLabel(
  answer: string,
  uiAction: LlmChatUiActionAgentQuestion,
): string {
  const matched = uiAction.options.find((option) => option.value === answer);
  return matched?.label ?? answer;
}

export function LlmChatAgentQuestionActions({
  uiAction,
  disabled,
  onSelect,
  className,
}: LlmChatAgentQuestionActionsProps) {
  const { t } = useTranslation();
  const [customAnswer, setCustomAnswer] = useState('');

  const submitCustomAnswer = () => {
    const trimmed = customAnswer.trim();
    if (!trimmed || disabled) return;
    onSelect(trimmed);
  };

  return (
    <div className={cn('mt-3 space-y-2 border-t border-border/60 pt-3', className)}>
      <p className="text-sm font-medium">{uiAction.question}</p>
      <div className="flex flex-col gap-2">
        {uiAction.options.map((option) => (
          <Button
            key={option.value}
            type="button"
            variant="outline"
            size="sm"
            className="h-auto flex-col items-start gap-0.5 whitespace-normal py-2 text-left"
            disabled={disabled}
            onClick={() => onSelect(option.value)}
          >
            <span className="font-medium">{option.label}</span>
            {option.description && (
              <span className="text-xs font-normal text-muted-foreground">{option.description}</span>
            )}
          </Button>
        ))}
      </div>

      {uiAction.allowCustomAnswer && (
        <div className="space-y-2 pt-1">
          <p className="text-xs text-muted-foreground">{t('llmChat.agentQuestion.customAnswerLabel')}</p>
          <div className="flex gap-2">
            <Input
              value={customAnswer}
              onChange={(event) => setCustomAnswer(event.target.value)}
              placeholder={t('llmChat.agentQuestion.customAnswerPlaceholder')}
              disabled={disabled}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  event.preventDefault();
                  submitCustomAnswer();
                }
              }}
            />
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={disabled || !customAnswer.trim()}
              onClick={submitCustomAnswer}
            >
              {t('llmChat.agentQuestion.customAnswerSubmit')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
